import { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Info, Search, X, Calendar, User, 
  Sparkles, Bot, FileText, Settings, 
  BookOpen, Wand2, Copy, Check, ChevronRight,
  MessageSquare, ShoppingCart, Headphones, 
  GraduationCap, Heart,
  Grid, List, Star, TrendingUp
} from 'lucide-react';
import Modal from '../ui/Modal';

// Mock de templates de agentes
const AGENT_TEMPLATES = [
  {
    id: 'vendas',
    name: 'Sofia - Vendas',
    category: 'vendas',
    description: 'Especialista em vendas e conversão de clientes',
    avatar: '👩‍💼',
    rating: 4.8,
    usageCount: 1250,
    instruction: `Você é Sofia, uma consultora de vendas amigável e experiente da {{agent_name}}.

Seu objetivo principal é ajudar os clientes a encontrar o produto perfeito para suas necessidades, sempre mantendo um tom acolhedor e profissional.

PERSONALIDADE:
- Seja empática e atenciosa
- Use linguagem positiva e encorajadora
- Demonstre entusiasmo genuíno pelos produtos
- Seja paciente com dúvidas

PROCESSO DE ATENDIMENTO:
1. Cumprimente calorosamente o cliente
2. Identifique suas necessidades através de perguntas abertas
3. Apresente soluções personalizadas
4. Esclareça dúvidas com clareza
5. Guie naturalmente para a conversão

INFORMAÇÕES IMPORTANTES:
- Horário atual: {{current_time}}
- Data: {{current_date}}
- Sempre mencione promoções ativas
- Destaque benefícios antes de preços
- Use exemplos práticos

Lembre-se: cada cliente é único e merece atenção personalizada!`
  },
  {
    id: 'suporte',
    name: 'Carlos - Suporte',
    category: 'suporte',
    description: 'Resolutivo e técnico para suporte ao cliente',
    avatar: '👨‍💻',
    rating: 4.9,
    usageCount: 2340,
    instruction: `Você é Carlos, especialista em suporte técnico da {{agent_name}}.

Sua missão é resolver problemas dos clientes de forma rápida e eficiente, sempre com paciência e clareza.

ABORDAGEM:
- Seja direto e objetivo nas soluções
- Use linguagem simples, evite termos técnicos
- Demonstre empatia com a frustração do cliente
- Confirme sempre se o problema foi resolvido

PROTOCOLO DE ATENDIMENTO:
1. Identifique o problema específico
2. Peça informações relevantes (número do pedido, etc)
3. Ofereça solução passo a passo
4. Acompanhe até a resolução completa
5. Pergunte se há mais algo em que possa ajudar

RECURSOS:
- Data/Hora: {{current_date}} às {{current_time}}
- Sempre documente o atendimento
- Escale para humano se necessário
- Mantenha histórico da conversa

Objetivo: Cliente satisfeito e problema resolvido!`
  },
  {
    id: 'agendamento',
    name: 'Marina - Agendamentos',
    category: 'agendamento',
    description: 'Organizada e eficiente para agendamentos',
    avatar: '📅',
    rating: 4.7,
    usageCount: 890,
    instruction: `Sou Marina, sua assistente de agendamentos da {{agent_name}}.

Estou aqui para tornar seu agendamento rápido e conveniente!

CARACTERÍSTICAS:
- Organizada e detalhista
- Flexível com horários
- Confirma sempre os dados
- Envia lembretes automáticos

FLUXO DE AGENDAMENTO:
1. Cumprimente e pergunte o serviço desejado
2. Apresente horários disponíveis
3. Colete dados necessários (nome, telefone, email)
4. Confirme todos os detalhes
5. Envie confirmação do agendamento

INFORMAÇÕES:
- Hoje é {{current_date}}
- Horário atual: {{current_time}}
- Sempre ofereça alternativas de horário
- Seja flexível com remarcarções
- Explique políticas de cancelamento

Compromisso: Agenda organizada, cliente feliz!`
  },
  {
    id: 'educador',
    name: 'Paulo - Educador',
    category: 'educacao',
    description: 'Didático e paciente para ensino e treinamento',
    avatar: '🎓',
    rating: 4.9,
    usageCount: 670,
    instruction: `Olá! Sou Paulo, seu educador virtual da {{agent_name}}.

Meu papel é ensinar e esclarecer dúvidas de forma clara e didática.

METODOLOGIA:
- Explique conceitos do simples ao complexo
- Use exemplos práticos e analogias
- Seja paciente com diferentes níveis de conhecimento
- Incentive perguntas

ESTRUTURA DE ENSINO:
1. Avalie o nível de conhecimento do aluno
2. Apresente o conteúdo gradualmente
3. Use exemplos do dia a dia
4. Verifique a compreensão
5. Reforce pontos importantes

DICAS:
- Data: {{current_date}}
- Adapte a linguagem ao público
- Repita informações importantes
- Celebre o progresso do aluno
- Ofereça recursos adicionais

Meta: Aprendizado efetivo e prazeroso!`
  },
  {
    id: 'recepcao',
    name: 'Amanda - Recepção',
    category: 'recepcao',
    description: 'Acolhedora para primeira impressão perfeita',
    avatar: '🌟',
    rating: 4.6,
    usageCount: 1560,
    instruction: `Bem-vindo! Sou Amanda, recepcionista virtual da {{agent_name}}.

É um prazer recebê-lo e direcioná-lo para o melhor atendimento!

POSTURA:
- Seja a melhor primeira impressão
- Cordial e profissional
- Proativa em ajudar
- Conhece bem todos os serviços

ATENDIMENTO:
1. Saudação calorosa e personalizada
2. Identifique a necessidade
3. Direcione para o setor correto
4. Forneça informações básicas
5. Deseje um ótimo atendimento

INFORMAÇÕES ÚTEIS:
- Horário: {{current_time}}
- Data: {{current_date}}
- Conheça todos os departamentos
- Tenha respostas para perguntas frequentes
- Seja o cartão de visitas da empresa

Lema: Toda visita merece ser especial!`
  }
];

// Componente de Card de Template
const TemplateCard = ({ template, onSelect, isSelected }) => {
  const getCategoryColor = (category) => {
    const colors = {
      vendas: 'from-green-500 to-pink-500',
      suporte: 'from-blue-500 to-cyan-500',
      agendamento: 'from-green-500 to-emerald-500',
      educacao: 'from-orange-500 to-yellow-500',
      recepcao: 'from-indigo-500 to-purple-500'
    };
    return colors[category] || 'from-gray-500 to-gray-600';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      vendas: <ShoppingCart className="w-4 h-4" />,
      suporte: <Headphones className="w-4 h-4" />,
      agendamento: <Calendar className="w-4 h-4" />,
      educacao: <GraduationCap className="w-4 h-4" />,
      recepcao: <Heart className="w-4 h-4" />
    };
    return icons[category] || <Bot className="w-4 h-4" />;
  };

  return (
    <button
      onClick={() => onSelect(template)}
      className={`w-full text-left p-6 bg-white rounded-2xl border-2 transition-all transform hover:scale-[1.02] ${
        isSelected 
          ? 'border-indigo-500 shadow-xl ring-4 ring-indigo-100' 
          : 'border-gray-200 hover:border-indigo-300 hover:shadow-lg'
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${getCategoryColor(template.category)} p-0.5 flex-shrink-0`}>
          <div className="w-full h-full bg-white rounded-2xl flex items-center justify-center text-2xl">
            {template.avatar}
          </div>
        </div>
        
        {/* Conteúdo */}
        <div className="flex-1">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h4 className="font-bold text-gray-900 text-lg">{template.name}</h4>
              <p className="text-sm text-gray-600 mt-1">{template.description}</p>
            </div>
            {isSelected && (
              <div className="bg-indigo-100 rounded-full p-1.5">
                <Check className="w-4 h-4 text-green-600" />
              </div>
            )}
          </div>
          
          {/* Métricas */}
          <div className="flex items-center gap-4 mt-3">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-yellow-500 fill-current" />
              <span className="text-sm font-semibold text-gray-700">{template.rating}</span>
            </div>
            <div className="flex items-center gap-1">
              <TrendingUp className="w-4 h-4 text-green-500" />
              <span className="text-sm text-gray-600">{template.usageCount} usos</span>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full">
              {getCategoryIcon(template.category)}
              <span className="text-xs font-medium text-gray-700 capitalize">{template.category}</span>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
};

// Modal de Templates
const TemplateModal = ({ isOpen, onClose, onSelectTemplate }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  
  const categories = [
    { id: 'all', name: 'Todos', icon: <Sparkles className="w-4 h-4" />, count: AGENT_TEMPLATES.length },
    { id: 'vendas', name: 'Vendas', icon: <ShoppingCart className="w-4 h-4" />, count: 1 },
    { id: 'suporte', name: 'Suporte', icon: <Headphones className="w-4 h-4" />, count: 1 },
    { id: 'agendamento', name: 'Agendamento', icon: <Calendar className="w-4 h-4" />, count: 1 },
    { id: 'educacao', name: 'Educação', icon: <GraduationCap className="w-4 h-4" />, count: 1 },
    { id: 'recepcao', name: 'Recepção', icon: <Heart className="w-4 h-4" />, count: 1 },
  ];
  
  const filteredTemplates = AGENT_TEMPLATES.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  
  const handleConfirm = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate);
      onClose();
      setSelectedTemplate(null);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white max-w-6xl w-full max-h-[90vh] flex flex-col animate-fade-in-fast">
        {/* Header com gradiente */}
        <div className="bg-gradient-to-r from-green-800 to-green-600 px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                <BookOpen className="w-8 h-8" />
                Central de Templates
              </h2>
              <p className="text-white/80 text-lg mt-1">
                Comece com um modelo pronto e personalize do seu jeito
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all ${
                  viewMode === 'grid' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-all ${
                  viewMode === 'list' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="ml-2 p-2 hover:bg-white/20 rounded-xl transition-all"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>
          </div>
          
          {/* Barra de pesquisa */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar templates por nome ou descrição..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 text-lg bg-white/95 backdrop-blur rounded-2xl border-2 border-transparent focus:border-white/50 focus:outline-none focus:ring-4 focus:ring-white/20 transition-all"
            />
          </div>
        </div>
        
        {/* Container principal */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar de categorias */}
          <div className="w-64 bg-gray-50 border-r border-gray-200 p-4 overflow-y-auto">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Categorias
            </h3>
            <div className="space-y-1">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-indigo-500 to-blue-500 text-white shadow-lg'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {category.icon}
                    <span className="font-medium">{category.name}</span>
                  </div>
                  <span className={`text-sm font-semibold px-2 py-1 rounded-full ${
                    selectedCategory === category.id
                      ? 'bg-white/20'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {category.count}
                  </span>
                </button>
              ))}
            </div>
            
            {/* Dica */}
            <div className="mt-8 p-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl border border-indigo-100">
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-green-600" />
                Dica Pro
              </h4>
              <p className="text-sm text-gray-600">
                Escolha um template como base e depois personalize com suas próprias instruções!
              </p>
            </div>
          </div>
          
          {/* Grid de templates */}
          <div className="flex-1 p-6 overflow-y-auto bg-gray-50/50">
            {filteredTemplates.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <Search className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Nenhum template encontrado
                </h3>
                <p className="text-gray-600">
                  Tente buscar por outros termos ou explore outras categorias
                </p>
              </div>
            ) : (
              <div className={
                viewMode === 'grid' 
                  ? "grid grid-cols-1 lg:grid-cols-2 gap-4"
                  : "space-y-4"
              }>
                {filteredTemplates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    onSelect={setSelectedTemplate}
                    isSelected={selectedTemplate?.id === template.id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Footer com ações */}
        <div className="px-8 py-6 border-t-2 border-gray-100 bg-white rounded-b-3xl flex items-center justify-between">
          <div className="text-gray-600">
            {selectedTemplate ? (
              <p className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                Template selecionado: <strong>{selectedTemplate.name}</strong>
              </p>
            ) : (
              <p>Selecione um template para começar</p>
            )}
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-2xl font-semibold transition-all"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedTemplate}
              className={`px-6 py-3 rounded-2xl font-semibold transition-all flex items-center gap-2 ${
                selectedTemplate
                  ? 'bg-gradient-to-r from-green-800 to-green-600 text-white hover:from-green-700 hover:to-green-700 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Copy className="w-5 h-5" />
              Usar Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente principal do Editor
const SystemPromptEditor = ({ value, onChange, tools, agentId }) => {
  const textareaRef = useRef(null);
  const [cursorPosition, setCursorPosition] = useState(null);
  const [showVariablesModal, setShowVariablesModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedVariable, setCopiedVariable] = useState(null);
  
  // Variáveis disponíveis traduzidas
  const availableVariables = [
    {
      category: 'Informações do Sistema',
      icon: <Settings className="w-5 h-5 text-green-600" />,
      variables: [
        {
          label: 'Nome do Funcionário',
          value: '{{agent_name}}',
          description: 'O nome configurado para este funcionário digital',
          example: 'Ex: Maria, João, Assistente Virtual'
        },
        {
          label: 'Data de Hoje',
          value: '{{current_date}}',
          description: 'A data atual no formato completo',
          example: 'Ex: 11 de agosto de 2025'
        },
        {
          label: 'Horário Atual',
          value: '{{current_time}}',
          description: 'O horário exato do momento',
          example: 'Ex: 14:30'
        }
      ]
    }
  ];
  
  // Filtrar variáveis
  const filteredVariables = availableVariables.map(category => ({
    ...category,
    variables: category.variables.filter(v => 
      v.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.value.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.variables.length > 0);
  
  // Inserir variável no texto
  const insertVariable = (variable) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = value;
    
    const newText = text.substring(0, start) + variable.value + text.substring(end);
    onChange(newText);
    
    // Reposicionar cursor
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + variable.value.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
    
    // Feedback visual
    setCopiedVariable(variable.value);
    setTimeout(() => setCopiedVariable(null), 2000);
  };
  
  // Aplicar template
  const handleSelectTemplate = (template) => {
    onChange(template.instruction);
    setShowTemplatesModal(false);
  };
  
// Adicione esta função no componente
const adjustTextareaHeight = useCallback(() => {
  const textarea = textareaRef.current;
  if (!textarea) return;
  
  // Salva posição do scroll para restaurar depois
  const scrollPos = window.pageYOffset;
  
  // Reset altura para calcular corretamente
  textarea.style.height = 'auto';
  
  // Calcula nova altura baseada no conteúdo
  const scrollHeight = textarea.scrollHeight;
  const minHeight = 700; // Altura mínima em pixels
  
  // Define a altura (maior entre mínima e necessária)
  textarea.style.height = `${Math.max(minHeight, scrollHeight)}px`;
  
  // Restaura posição do scroll
  window.scrollTo(0, scrollPos);
}, []);

// Use o useEffect para chamar quando o valor mudar
useEffect(() => {
  adjustTextareaHeight();
}, [value, adjustTextareaHeight]);

// Também ajuste na montagem inicial
useEffect(() => {
  adjustTextareaHeight();
  // Adiciona listener para resize da janela
  window.addEventListener('resize', adjustTextareaHeight);
  return () => window.removeEventListener('resize', adjustTextareaHeight);
}, [adjustTextareaHeight]);

  // Contar palavras
  const wordCount = value.trim().split(/\s+/).filter(word => word.length > 0).length;
  
  return (
    <div className="space-y-6">
      {/* Header com ações */}
      <div className="bg-white rounded-3xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6 p-2">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <FileText className="w-8 h-8 text-green-600" />
              Instruções de Trabalho
            </h3>
            <p className="text-gray-600 mt-1">
              Explique detalhadamente como o funcionário deve se comportar, suas responsabilidades e personalidade.
            </p>
          </div>
          
          {/* Botões de ação */}
          <div className="flex gap-3">
            <button
              onClick={() => setShowTemplatesModal(true)}
              className="px-5 py-3 bg-gradient-to-r from-green-600 to-green-600 text-white rounded-2xl font-semibold hover:from-green-700 hover:to-green-700 transition-all flex items-center gap-2 shadow-lg transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <BookOpen className="w-5 h-5" />
              Usar Template
            </button>
            
            <button
              onClick={() => setShowVariablesModal(true)}
              className="px-5 py-3 bg-white border-2 border-indigo-300 text-green-600 rounded-2xl font-semibold hover:bg-indigo-50 transition-all flex items-center gap-2"
            >
              <Wand2 className="w-5 h-5" />
              Adicionar Variável
            </button>
          </div>
        </div>
        
        {/* Editor principal estilo Word */}
        <div className="bg-gray-50 rounded-2xl border-2 border-gray-200 overflow-hidden focus-within:border-indigo-500 focus-within:ring-4 focus-within:ring-indigo-100 transition-all">
          {/* Barra de ferramentas simples */}
          <div className="bg-white px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-600">
                {wordCount} palavras
              </span>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => setShowVariablesModal(true)}
                className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
              >
                <Sparkles className="w-4 h-4" />
                Inserir variável dinâmica
              </button>
            </div>
            
            {copiedVariable && (
              <div className="flex items-center gap-2 text-green-600 text-sm font-medium animate-fade-in">
                <Check className="w-4 h-4" />
                Variável {copiedVariable} inserida!
              </div>
            )}
          </div>
          
          {/* Textarea grande estilo documento */}
          <textarea
  ref={textareaRef}
  value={value}
  onChange={(e) => {
    onChange(e.target.value);
    adjustTextareaHeight(); // Ajusta imediatamente ao digitar
  }}
  className="w-full px-8 py-6 bg-white text-gray-800 text-lg leading-relaxed focus:outline-none resize-none overflow-hidden transition-none"
  style={{ 
    minHeight: '700px', 
    fontFamily: 'Georgia, serif'
  }}
  placeholder={`Escreva aqui as instruções detalhadas para seu funcionário digital...

Por exemplo:
- Como deve cumprimentar os clientes
- Qual tom de voz usar (formal, informal, amigável)
- Quais informações sempre fornecer
- Como lidar com situações específicas

Dica: Use variáveis dinâmicas como {{agent_name}} para personalizar as respostas!`}
/>
        </div>
        
        {/* Dicas contextuais */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-100">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-xl">
                <MessageSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Tom de Voz</h4>
                <p className="text-sm text-gray-600">
                  Defina se o funcionário deve ser formal, casual ou técnico
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-4 border border-green-100">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-xl">
                <User className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Personalidade</h4>
                <p className="text-sm text-gray-600">
                  Dê características humanas como empático, prestativo, objetivo
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-pink-50 rounded-2xl p-4 border border-purple-100">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-100 rounded-xl">
                <Sparkles className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Variáveis</h4>
                <p className="text-sm text-gray-600">
                  {"Use {{variáveis}} para informações que mudam dinamicamente"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal 
        isOpen={showTemplatesModal} 
        onClose={() => setShowTemplatesModal(false)}
        size="xl"
        title=""
      >
        {/* Modal de Templates */}
        <TemplateModal
          isOpen={showTemplatesModal}
          onClose={() => setShowTemplatesModal(false)}
          onSelectTemplate={handleSelectTemplate}
        />
      </Modal>
      
      {/* Modal de Variáveis Simplificado */}
      {showVariablesModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col animate-fade-in-fast">
            {/* Header */}
            <div className="px-6 py-5 border-b-2 border-gray-100 bg-gradient-to-r from-indigo-50 to-blue-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 flex items-center gap-3">
                    <Wand2 className="w-6 h-6 text-green-600" />
                    Variáveis Dinâmicas
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Informações que se atualizam automaticamente
                  </p>
                </div>
                <button 
                  className="p-2 hover:bg-white/80 rounded-xl transition-all"
                  onClick={() => setShowVariablesModal(false)}
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            
            {/* Barra de Pesquisa */}
            <div className="px-6 py-4 border-b bg-white">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar variáveis..."
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all text-base"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
            
            {/* Lista de Variáveis */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
              {filteredVariables.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">Nenhuma variável encontrada</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredVariables.map((category, idx) => (
                    <div key={idx}>
                      <div className="flex items-center gap-2 mb-3">
                        {category.icon}
                        <h4 className="font-semibold text-gray-800">{category.category}</h4>
                      </div>
                      
                      <div className="space-y-3">
                        {category.variables.map((variable, vIdx) => (
                          <button
                            key={vIdx}
                            onClick={() => {
                              insertVariable(variable);
                              setShowVariablesModal(false);
                            }}
                            className="w-full text-left p-4 bg-white rounded-xl border-2 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all group"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="font-semibold text-gray-900 mb-1">
                                  {variable.label}
                                </div>
                                <div className="text-sm text-gray-600 mb-2">
                                  {variable.description}
                                </div>
                                <div className="flex items-center gap-4">
                                  <code className="text-xs bg-indigo-100 text-green-700 px-3 py-1 rounded-lg font-mono">
                                    {variable.value}
                                  </code>
                                  <span className="text-xs text-gray-500">
                                    {variable.example}
                                  </span>
                                </div>
                              </div>
                              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-green-600 mt-1" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Footer com dica */}
            <div className="px-6 py-4 border-t bg-gradient-to-r from-indigo-50 to-blue-50">
              <div className="flex items-center gap-3">
                <Info className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p className="text-sm text-gray-700">
                  <strong>Dica:</strong> Clique em qualquer variável para inserir no texto. 
                  As variáveis são atualizadas automaticamente durante a conversa!
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Estilos de animação */}
      <style jsx>{`
        @keyframes fade-in-fast {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in-fast {
          animation: fade-in-fast 0.2s ease-out;
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SystemPromptEditor;